import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../assets/logo.png';
import hidevLogo from '../assets/hidev_logo.png';
import SocialLinks from './SocialLinks';
import { useEffect, useState } from 'react';

interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <footer className={`footer ${className}`} style={{
      backgroundColor: 'var(--pure-white)',
      padding: 'var(--spacing-xxl) 0 var(--spacing-lg)',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: 'var(--font-secondary)',
      boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)'
    }}>
      <div className="container">
        {/* Footer Content with Grid Layout */}
        <div className="footer__content" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? 'var(--spacing-md)' : 'var(--spacing-xl)',
          alignItems: 'center',
          marginBottom: 'var(--spacing-xl)',
          position: 'relative',
          textAlign: isMobile ? 'center' : 'inherit'
        }}>
          {/* Logo Container - Left */}
          <div className="footer__logo-container" style={{
            display: 'flex',
            alignItems: isMobile ? 'center' : 'flex-start',
            justifyContent: isMobile ? 'center' : 'flex-start',
            marginBottom: isMobile ? 'var(--spacing-md)' : '0',
            gridRow: isMobile ? '1' : 'auto',
            gridColumn: isMobile ? '1' : 'auto'
          }}>
            <Link to="/" className="footer__logo" style={{
              display: 'block',
              width: isMobile ? '140px' : '150px',
              transition: 'transform var(--transition-medium)'
            }}>
              <img
                src={logo}
                alt="Airline Crew Trans"
                style={{
                  width: '100%',
                  height: 'auto'
                }}
              />
            </Link>
          </div>

          {/* Navigation - Center */}
          <nav className="footer__nav" style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'center' : 'space-around',
            textAlign: 'center',
            gap: isMobile ? 'var(--spacing-lg)' : 'inherit',
            marginBottom: isMobile ? 'var(--spacing-xl)' : '0',
            gridRow: isMobile ? '2' : 'auto',
            gridColumn: isMobile ? '1' : 'auto'
          }}>
            <div className="footer__nav-column" style={{ flex: 1 }}>
              <ul className="footer__nav-list" style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                <li className="footer__nav-item" style={{ marginBottom: 'var(--spacing-md)' }}>
                  <Link to="/" className="footer__nav-link" style={{
                    fontFamily: 'var(--font-secondary)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--rich-black)',
                    textDecoration: 'none',
                    fontWeight: 'var(--font-weight-semi-bold)',
                    letterSpacing: '0.5px',
                    position: 'relative',
                    transition: 'all var(--transition-quick)',
                    paddingBottom: '3px'
                  }}>{t('home')}</Link>
                </li>
                <li className="footer__nav-item" style={{ marginBottom: 'var(--spacing-md)' }}>
                  <a href="/#about" className="footer__nav-link" style={{
                    fontFamily: 'var(--font-secondary)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--rich-black)',
                    textDecoration: 'none',
                    fontWeight: 'var(--font-weight-semi-bold)',
                    letterSpacing: '0.5px',
                    position: 'relative',
                    transition: 'all var(--transition-quick)',
                    paddingBottom: '3px'
                  }}>{t('about_us')}</a>
                </li>
                <li className="footer__nav-item" style={{ marginBottom: 'var(--spacing-md)' }}>
                  <a href="/#services" className="footer__nav-link" style={{
                    fontFamily: 'var(--font-secondary)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--rich-black)',
                    textDecoration: 'none',
                    fontWeight: 'var(--font-weight-semi-bold)',
                    letterSpacing: '0.5px',
                    position: 'relative',
                    transition: 'all var(--transition-quick)',
                    paddingBottom: '3px'
                  }}>{t('services')}</a>
                </li>
              </ul>
            </div>
            <div className="footer__nav-column" style={{ flex: 1 }}>
              <ul className="footer__nav-list" style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                <li className="footer__nav-item" style={{ marginBottom: 'var(--spacing-md)' }}>
                  <a href="/#contact" className="footer__nav-link" style={{
                    fontFamily: 'var(--font-secondary)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--rich-black)',
                    textDecoration: 'none',
                    fontWeight: 'var(--font-weight-semi-bold)',
                    letterSpacing: '0.5px',
                    position: 'relative',
                    transition: 'all var(--transition-quick)',
                    paddingBottom: '3px'
                  }}>{t('contact')}</a>
                </li>
                <li className="footer__nav-item" style={{ marginBottom: 'var(--spacing-md)' }}>
                  <Link to="/our-locations" className="footer__nav-link" style={{
                    fontFamily: 'var(--font-secondary)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--rich-black)',
                    textDecoration: 'none',
                    fontWeight: 'var(--font-weight-semi-bold)',
                    letterSpacing: '0.5px',
                    position: 'relative',
                    transition: 'all var(--transition-quick)',
                    paddingBottom: '3px'
                  }}>{t('locations')}</Link>
                </li>
              </ul>
            </div>
          </nav>

          {/* HiDev Logo - Right */}
          <div className="footer__hidev" style={{
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'flex-end',
            alignItems: 'center',
            marginTop: isMobile ? 'var(--spacing-md)' : 0,
            gridRow: isMobile ? '3' : 'auto',
            gridColumn: isMobile ? '1' : 'auto'
          }}>
            <a
              href="https://hidevmobile.com"
              target="_blank"
              rel="noopener noreferrer"
              className="footer__hidev-link"
              style={{
                display: isMobile ? 'inline-block' : 'block',
                width: isMobile ? '90px' : '100px',
                transition: 'all 0.3s ease'
              }}
            >
              <img
                src={hidevLogo}
                alt="HiDev"
                className="footer__hidev-logo"
                style={{
                  width: '100%',
                  height: 'auto',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
                  margin: isMobile ? '0 auto' : 'inherit'
                }}
              />
            </a>
          </div>
        </div>

        {/* Social Links */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: 'var(--spacing-lg)',
          maxWidth: '100%',
          overflow: 'visible',
          padding: '0 10px'
        }}>
          <div style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <SocialLinks iconSize="lg" />
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer__bottom" style={{
          textAlign: 'center',
          position: 'relative',
          marginTop: isMobile ? 'var(--spacing-lg)' : 'inherit',
          paddingTop: isMobile ? 'var(--spacing-lg)' : 'var(--spacing-md)'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: '15%',
            width: '70%',
            height: '1px',
            background: 'linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,0.15), rgba(0,0,0,0))'
          }}></div>
          <p className="footer__copyright" style={{
            fontFamily: 'var(--font-secondary)',
            fontSize: isMobile ? '0.75rem' : 'var(--font-size-small)',
            color: 'var(--dark-gray)',
            margin: 0,
            letterSpacing: '0.5px'
          }}>
            &copy; {year} {t('footer_copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 